import classNames from 'classnames';
import {IInputCaptionPublicProps, InputCaption} from 'components/InputCaption';
import React, {
    ChangeEvent,
    ChangeEventHandler,
    InputHTMLAttributes,
    MouseEventHandler,
    ReactElement,
    ReactNode,
    Ref,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useState,
} from 'react';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import {Loader} from 'components/Loader';
import Icon from 'components/Icon';

type TInputValue = InputHTMLAttributes<HTMLInputElement>['value'];

export interface IInputProps<V extends TInputValue = string>
    extends InputHTMLAttributes<HTMLInputElement>, IInputCaptionPublicProps {
    labelText?: string;
    name?: string;
    value?: V;
    disabled?: boolean;
    placeholder?: string;
    startIcon?: ReactNode;
    trailIcon?: ReactNode;
    trailIconClassname?: string;
    inputClassname?: string;
    onExtraActionClick?: MouseEventHandler<HTMLDivElement>;
    type?: InputHTMLAttributes<HTMLInputElement>['type'];
    isNeedCaption?: boolean;
    notFadingPlaceholder?: string;
    isLoading?: boolean;
    isClearable?: boolean;
    showErrorText?: boolean;
    onlyNumbers?: boolean;
    classNamesMap?: {
        inputLabel?: string;
    };
    inputRef?: Ref<HTMLInputElement>;
}

// eslint-disable-next-line complexity,max-lines-per-function
const InputOriginal = <V extends TInputValue>(props: IInputProps<V>, ref?: Ref<HTMLInputElement>) => {
    const {
        className,
        inputClassname,
        captionText,
        errorText,
        labelText,
        onChange,
        showErrorText = true,
        value,
        type = 'text',
        trailIcon,
        startIcon,
        trailIconClassname,
        onExtraActionClick,
        isNeedCaption = true,
        isLoading = false,
        notFadingPlaceholder,
        isClearable = false,
        onlyNumbers,
        classNamesMap,
        inputRef,
        disabled,
        ...restInputAttributes
    } = props;
    const uid = useId();
    const [isInputFilled, setIsInputFilled] = useState<boolean>(Boolean(value?.toString()));
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
    const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null);

    useEffect(() => {
        setIsInputFilled(Boolean(value?.toString()));
    }, [value]);

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (disabled) {
                return;
            }

            const val = event.target.value;

            if (onlyNumbers) {
                const numberValue = Number(val);

                if (Number.isNaN(numberValue)) {
                    return;
                }
            }

            setIsInputFilled(Boolean(val));
            onChange?.(event);
        },
        [onChange, onlyNumbers, disabled],
    );

    const renderPlaceholder = useMemo<ReactNode>(() => {
        if (!notFadingPlaceholder) {
            return '';
        }
        return (
            <>
                <Typography className={style.notFadingPlaceholderTransparent} variant={'p'}>
                    {value}
                </Typography>
                <Typography color={'gray50'} variant={'p'}>
                    {notFadingPlaceholder.slice(String(value).length)}
                </Typography>
            </>
        );
    }, [notFadingPlaceholder, value]);

    const assignRef = useCallback((targetRef: Ref<HTMLInputElement> | undefined, node: HTMLInputElement | null) => {
        if (!targetRef) {
            return;
        }
        if ('function' === typeof targetRef) {
            targetRef(node);
            return;
        }
        try {
            Reflect.set(targetRef as object, 'current', node);
        } catch {}
    }, []);

    const setMergedRefs = useCallback(
        (node: HTMLInputElement | null) => {
            setInputNode(node ?? null);
            assignRef(ref, node);
            assignRef(inputRef, node);
        },
        [ref, inputRef, assignRef],
    );

    const inputRefValue = inputNode?.value;

    const handleValueClear = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
        if (inputNode) {
            const event = {target: inputNode} as ChangeEvent<HTMLInputElement>;
            handleInputChange(event);

            inputNode.value = '';
            inputNode.blur();

            if (onChange) {
                onChange(event);
            }
        }
    }, [handleInputChange, inputNode, onChange]);

    const handleContainerMouseDownCapture = useCallback<MouseEventHandler<HTMLLabelElement>>(
        (event) => {
            if (!inputNode || document.activeElement !== inputNode) {
                return;
            }

            if (event.target === inputNode) {
                return;
            }

            event.preventDefault();
        },
        [inputNode],
    );

    // определяем, нужно ли показывать кнопку очистки
    const shouldShowClearButton = useMemo(
        () => isClearable && isInputFocused && inputRefValue,
        [inputRefValue, isClearable, isInputFocused],
    );

    return (
        <div
            className={className}
            data-skeleton-item={(restInputAttributes as {[key: string]: any})['data-skeleton-item']}
        >
            <label
                className={classNames(style.input, {
                    [style.inputFocused]: isInputFocused,
                    [style.inputDisabled]: disabled,
                    [style.inputWithExtraAction]: startIcon || trailIcon || shouldShowClearButton,
                    [style.inputHasValue]: isInputFilled,
                })}
                htmlFor={uid}
                onMouseDownCapture={handleContainerMouseDownCapture}
            >
                {startIcon && <div className={style.inputStartIcon}>{startIcon}</div>}

                <input
                    {...restInputAttributes}
                    className={classNames(style.inputOriginal, inputClassname, {
                        [style.inputOriginalFilled]: isInputFilled,
                        [style.inputOriginalNoLabel]: !labelText,
                        [style.transparent]: Boolean(notFadingPlaceholder),
                    })}
                    onFocus={(e) => {
                        restInputAttributes?.onFocus?.(e);
                        setIsInputFocused(true);
                    }}
                    onBlur={(e) => {
                        restInputAttributes?.onBlur?.(e);
                        setIsInputFocused(false);
                    }}
                    id={uid}
                    onChange={handleInputChange}
                    ref={setMergedRefs}
                    type={type}
                    value={value}
                    disabled={disabled}
                />
                <div
                    className={classNames(style.inputFrame, {
                        [style.inputFrameError]: errorText !== undefined,
                    })}
                />

                {notFadingPlaceholder && <span className={style.notFadingPlaceholder}>{renderPlaceholder}</span>}

                {labelText && (
                    <span className={classNames(style.inputLabel, classNamesMap?.inputLabel)}>{labelText}</span>
                )}

                {shouldShowClearButton && (
                    <button className={style.clearBtn} onClick={handleValueClear} type={'button'}>
                        <Icon icon={'NewCancel'} width={24} height={24} />
                    </button>
                )}

                {trailIcon && !shouldShowClearButton && (
                    <div className={classNames(style.trailIcon, trailIconClassname)} onClick={onExtraActionClick}>
                        {trailIcon}
                    </div>
                )}

                {isLoading && <Loader className={style.loader} />}
            </label>

            {isNeedCaption && (captionText || errorText) && (
                <InputCaption
                    captionText={captionText}
                    disabled={disabled}
                    errorText={showErrorText ? errorText : undefined}
                />
            )}
        </div>
    );
};

export const Input = React.forwardRef(InputOriginal) as <V extends TInputValue>(
    props: IInputProps<V> & {ref?: Ref<HTMLInputElement>},
) => ReactElement;
